/**
 * G-126 (finance audit WO-I, [155]; OD-154) — reads a number someone typed
 * into a money or percent box, the Spanish way or the English way, and refuses
 * what could be read both ways instead of guessing.
 *
 * Before this, each form did `Number(raw.replace(",", "."))`: "1.800" (one
 * thousand eight hundred, as written in Spain) was saved as €1.80 — for a
 * running cost, a salary payment and a monthly salary alike — and "1.800,50"
 * was refused. One reader now serves every pay and running-cost box.
 *
 *   1800 · 1800,50 · 1800.50      plain
 *   1.800 · 12.500 · 1 800        a dot or space before exactly three digits groups thousands
 *   1.800,50 · 1,800.50           both marks: the last one is the decimal
 *   12,5 · 12.50                  one or two digits after the mark are cents
 *   1,800 · 0.005 · 1.80.0        refused — three digits after a comma could be either
 */
export type TypedAmount = { ok: true; value: number } | { ok: false; message: string };

const SPACES = /[\s\u00a0\u202f]/g;
/** Not global: `.test` on a /g regex keeps `lastIndex` between calls. */
const HAS_SPACE = /[\s\u00a0\u202f]/;
const AMBIGUOUS = "Could be read two ways. Write 1800 for one thousand eight hundred, or 1,80 for one euro eighty.";
const UNREADABLE = "Use digits, with a comma or a dot before the cents (e.g. 1800 or 1800,50).";

/** `int` groups thousands with `mark` ("1.800.000") or has none ("1800"). */
function readInteger(int: string, mark: "." | "," | null): string | null {
  if (/^\d+$/.test(int)) return int;
  if (!mark) return null;
  const groups = int.split(mark);
  const [first, ...rest] = groups;
  if (!/^[1-9]\d{0,2}$/.test(first) || rest.length === 0 || !rest.every((g) => /^\d{3}$/.test(g))) {
    return null;
  }
  return groups.join("");
}

function done(normalized: string): TypedAmount {
  const value = Number(normalized);
  return Number.isFinite(value) ? { ok: true, value } : { ok: false, message: UNREADABLE };
}

export function parseTypedAmount(raw: string): TypedAmount {
  let s = raw.trim().replace(/^€\s*/, "").replace(/\s*€$/, "");
  if (s === "") return { ok: false, message: "Enter an amount." };

  if (HAS_SPACE.test(s)) {
    // A space only ever groups thousands (1 800, 12 500,50 — Russian / Ukrainian style).
    if (!/^[1-9]\d{0,2}(?:[\s\u00a0\u202f]\d{3})+(?:[.,]\d{1,2})?$/.test(s)) {
      return { ok: false, message: UNREADABLE };
    }
    s = s.replace(SPACES, "");
  }
  if (/[^\d.,]/.test(s)) return { ok: false, message: UNREADABLE };

  const lastDot = s.lastIndexOf(".");
  const lastComma = s.lastIndexOf(",");

  if (lastDot >= 0 && lastComma >= 0) {
    // Both marks: the last one is the decimal, the other groups thousands.
    const decimalAt = Math.max(lastDot, lastComma);
    const thousands = decimalAt === lastDot ? "," : ".";
    const int = readInteger(s.slice(0, decimalAt), thousands);
    const cents = s.slice(decimalAt + 1);
    if (int === null || !/^\d{1,2}$/.test(cents)) return { ok: false, message: UNREADABLE };
    return done(`${int}.${cents}`);
  }

  if (lastComma >= 0) {
    const [int, cents, extra] = s.split(",");
    if (extra !== undefined || !/^\d+$/.test(int)) return { ok: false, message: UNREADABLE };
    if (/^\d{3}$/.test(cents)) return { ok: false, message: AMBIGUOUS };
    if (!/^\d{1,2}$/.test(cents)) return { ok: false, message: UNREADABLE };
    return done(`${int}.${cents}`);
  }

  if (lastDot >= 0) {
    const thousands = readInteger(s, ".");
    if (thousands !== null) return done(thousands);
    const [int, cents, extra] = s.split(".");
    if (extra === undefined && /^\d+$/.test(int) && /^\d{1,2}$/.test(cents)) return done(`${int}.${cents}`);
    if (extra === undefined && /^\d{3}$/.test(cents)) return { ok: false, message: AMBIGUOUS };
    return { ok: false, message: UNREADABLE };
  }

  return done(s);
}

/**
 * What the app read, shown under the box before Save ("= €1800.00"), in the
 * same form the Finances tables print money — so a misread is visible while
 * it can still be corrected. `null` while the box is empty.
 */
export function amountReadBack(raw: string): string | null {
  if (raw.trim() === "") return null;
  const read = parseTypedAmount(raw);
  return read.ok ? `= €${read.value.toFixed(2)}` : read.message;
}

/**
 * A money box that may be left empty (a shift's pay rate, KI-109): empty is
 * "no value" (`null`), anything else is read by `parseTypedAmount` (which
 * already refuses a minus sign). The Schedule's rate boxes used their own
 * `Number(raw.replace(",", "."))`, which read "1.800" as €1.80.
 */
export function parseOptionalTypedAmount(
  raw: string,
): { ok: true; value: number | null } | { ok: false; message: string } {
  if (raw.trim() === "") return { ok: true, value: null };
  return parseTypedAmount(raw);
}
